import { useState, useEffect, useRef, useMemo, memo } from 'react';
import { motion } from 'framer-motion';
import { leaderboardAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Trophy } from 'lucide-react';
import { AnimatedNumber } from './AnimatedNumber';
import { RankDeltaBadge } from './RankDeltaBadge';
import { computeLeaderboardDeltas } from '../lib/rankAnim';
import { buildTeamRowAvatar } from '../lib/avatars';
import { formatFrancAdaptive } from '../lib/utils';

export default memo(function MiniStandings({ selectedLeagueId, onTeamClick }) {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  // v6.x — rank delta + "shuffle" animation (own snapshot key so the mini
  // board animates relative to ITS last view, independent of the full tab).
  const [rankDeltas, setRankDeltas] = useState({});
  const [orderMode, setOrderMode] = useState('current'); // 'prev' | 'current'
  const prevRanksRef = useRef(null);
  const prevPointsRef = useRef(null);
  const timerRef = useRef(null);
  // Guard: delta/snapshot write side-effect must run once per (league,user).
  const snapDoneForRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  useEffect(() => {
    // v5.7.63 — Honour the selected league. Previously hard-coded to global.
    const p = selectedLeagueId
      ? leaderboardAPI.get(selectedLeagueId)
      : leaderboardAPI.global();
    p.then(res => {
      const lb = res.data.leaderboard || [];
      const top5 = lb.slice(0, 5);
      setLeaderboard(top5);
      const mine = lb.find(t => t.user_id === user?.id);
      if (mine) setMyRank(mine);

      const snapKey = `rankMini:${selectedLeagueId || 'global'}:${user?.id || 'anon'}`;
      if (snapDoneForRef.current !== snapKey) {
        snapDoneForRef.current = snapKey;
        const { deltas, prevRanks, prevPoints, anyMoved } = computeLeaderboardDeltas(top5, snapKey);
        prevRanksRef.current = prevRanks;
        prevPointsRef.current = prevPoints;
        setRankDeltas(deltas);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (anyMoved) {
          setOrderMode('prev');
          timerRef.current = setTimeout(() => setOrderMode('current'), 140);
        } else {
          setOrderMode('current');
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user?.id, selectedLeagueId]);

  const ordered = useMemo(() => {
    if (orderMode === 'prev' && prevRanksRef.current) {
      return [...leaderboard].sort((a, b) =>
        (prevRanksRef.current[a.id] ?? a.rank ?? 9e9) - (prevRanksRef.current[b.id] ?? b.rank ?? 9e9)
      );
    }
    return leaderboard;
  }, [leaderboard, orderMode]);

  if (loading) return <Skeleton className="h-32 w-full rounded-2xl" />;

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4" data-testid="mini-standings">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-[hsl(var(--accent))]" />
          <h3 className="text-sm font-semibold">Mini Rankings</h3>
        </div>
        {myRank && (
          <Badge variant="secondary" className="text-[10px] tabular-nums flex items-center gap-1">
            Your rank: #{myRank.rank}
            <RankDeltaBadge delta={rankDeltas[myRank.id]} />
          </Badge>
        )}
      </div>

      <div className="space-y-1.5">
        {leaderboard.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">No rankings yet</p>
        )}
        {ordered.map((team, i) => {
          const rank = team.rank || i + 1;
          const isMe = team.user_id === user?.id;

          // v5.7.63 — Row is clickable; deep-links to the team's roster.
          const clickable = !!onTeamClick && !!team.id;
          const delta = rankDeltas[team.id];
          const prevPts = prevPointsRef.current?.[team.id];

          return (
            <motion.div
              key={team.id || i}
              layout
              transition={{ type: 'spring', stiffness: 600, damping: 42 }}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${
                isMe ? 'bg-[hsl(var(--accent)/0.1)] border border-[hsl(var(--accent)/0.2)]' : ''
              } ${isMe && delta ? 'rank-glow' : ''} ${clickable ? 'cursor-pointer hover:bg-secondary/40 active:scale-[0.99] transition-all' : ''}`}
              onClick={clickable ? () => onTeamClick(team.id) : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTeamClick(team.id); } } : undefined}
              data-testid={clickable ? `mini-standings-row-${rank}` : undefined}
            >
              <span className={`w-5 text-center text-xs font-bold tabular-nums shrink-0 ${
                rank <= 3 ? 'text-[hsl(var(--accent))]' : 'text-muted-foreground'
              }`}>
                {rank}
              </span>
              <img
                src={buildTeamRowAvatar(team, user, 48)}
                alt=""
                className="h-7 w-7 rounded-full bg-secondary object-cover shrink-0"
                data-testid={`mini-standings-avatar-${rank}`}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-xs truncate leading-tight ${isMe ? 'font-semibold' : 'font-medium'}`}>
                  {team.name || 'Unknown'}
                </p>
                {team.username && (
                  <p className="text-[10px] text-muted-foreground truncate leading-tight">@{team.username}</p>
                )}
              </div>
              <RankDeltaBadge delta={delta} testid={`mini-rank-delta-${rank}`} />
              <span className="text-xs font-bold tabular-nums shrink-0">
                <AnimatedNumber
                  value={team.net_worth || 0}
                  from={typeof prevPts === 'number' ? prevPts : undefined}
                  format={(n) => formatFrancAdaptive(n, { symbol: true })}
                  durationMs={900}
                  testid={`mini-points-${rank}`}
                />
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
});
